import useChartOptions from "features/Charting/hooks/useChartOptions";
import useChartSelections from "components/charting/hooks/useChartSelections";
import useSetChartSelections from "features/Charting/hooks/useSetChartSelections";
import { Metadata, MetadataType } from "types/metadata";
import { useCallback, useMemo } from "react";
import ExerciseMetadataService from "services/ExerciseMetadataService";
import ExerciseHelpers from "helpers/ExerciseHelpers";
import CircuitHelpers from "helpers/CircuitHelpers";
import { useTranslation } from "react-i18next";

function useChartMeasures() {
  const options = useChartOptions();
  const selections = useChartSelections();
  const setSelections = useSetChartSelections();
  const { t } = useTranslation();

  const setMeasureAtIndex = useCallback(
    (measureIndex: number) => (event: unknown, measure: Metadata | null) => {
      if (!measure) throw new Error("Should not be able to set null as a Measure value");

      setSelections((prev) => ({
        measures: prev.measures.map((prevMeasure, prevMeasureIndex) => {
          if (measureIndex !== prevMeasureIndex) {
            return prevMeasure;
          }

          if (measure.type === MetadataType.EXERCISE) {
            return {
              type: measure.type,
              typeLabel: t(`global.metadata_type_${MetadataType.EXERCISE}`),
              id: measure.metadata.id,
              metadata: measure.metadata,
              metric: ExerciseHelpers.getMetricByFieldNameOrDefault(measure.metadata, prevMeasure.metric?.field),
              repMetric: ExerciseMetadataService.repMetricByFieldNameOrFirstOrNull(
                measure.metadata,
                prevMeasure.repMetric?.field
              ),
            };
          }

          if (measure.type === MetadataType.ANTHROPOMETRIC) {
            return {
              type: measure.type,
              typeLabel: t(`global.metadata_type_${MetadataType.ANTHROPOMETRIC}`),
              id: measure.metadata.value,
              metadata: measure.metadata,
              metric: null,
              repMetric: null,
            };
          }

          if (measure.type === MetadataType.SCORE) {
            return {
              type: measure.type,
              typeLabel: t(`global.metadata_type_${MetadataType.SCORE}`),
              id: measure.metadata.id,
              metadata: measure.metadata,
              metric: null,
              repMetric: null,
            };
          }

          if (measure.type === MetadataType.WELLNESS) {
            return {
              type: measure.type,
              typeLabel: t(`global.metadata_type_${MetadataType.WELLNESS}`),
              id: measure.metadata.id,
              metadata: measure.metadata,
              metric: null,
              repMetric: null,
            };
          }

          if (measure.type === MetadataType.CIRCUIT) {
            return {
              type: measure.type,
              typeLabel: t(`global.metadata_type_${MetadataType.CIRCUIT}`),
              id: measure.metadata.id,
              metadata: measure.metadata,
              metric:
                CircuitHelpers.getMetricByFieldNameOrDefault(measure.metadata, prevMeasure.metric?.field) ??
                measure.metadata.metrics[0],
              repMetric: null,
            };
          }

          throw new Error("Unsupported chart measure type");
        }),
      }));
    },
    [t, setSelections]
  );

  return useMemo(
    () => ({
      options: options.measures,
      values: selections.measures,
      onChangeAtIndex: setMeasureAtIndex,
      getOptionLabel: (option: Metadata) =>
        option.type === MetadataType.ANTHROPOMETRIC ? option.metadata?.label : option.metadata?.name,
      groupBy: (option: Metadata) =>
        option.type === MetadataType.EXERCISE ? option.metadata?.category : option.typeLabel,
    }),
    [options.measures, selections.measures, setMeasureAtIndex]
  );
}
export default useChartMeasures;
